import {
  Editor,
  type Editor as EditorInstance,
  Element,
  type ElementEntry,
  Node,
  Path,
  Range,
  Transforms,
} from 'slate'
import type { BulletedListElement, NumberedListElement } from '../custom-types'
import { LIST_TYPES } from './withNormalisedLists'

function isList(node: Node): node is BulletedListElement | NumberedListElement {
  return Element.isElement(node) && LIST_TYPES.includes(node.type)
}

export function handleTabKey(
  editor: EditorInstance,
  event: React.KeyboardEvent<HTMLDivElement>,
) {
  if (!editor.selection) return
  event.preventDefault()

  if (Range.isCollapsed(editor.selection!)) {
    const [entry] = Editor.nodes(editor, {
      at: editor.selection!.anchor,
      match: (n) => Element.isElement(n) && n.type === 'list-item',
      mode: 'lowest',
    })

    if (entry) {
      let [item, itemPath] = entry
      const [list, listPath] = Editor.parent(editor, itemPath)
      if (!isList(list)) throw new Error('Invalid list element')
      const [parent] = Editor.parent(editor, listPath)

      if (event.shiftKey) {
        if (!Element.isElement(parent) || parent.type !== 'list-item') return

        // remove all items after our own from the list
        const childrenToInsert = []
        for (const [child, childPath] of Node.children(editor, listPath, {
          reverse: true,
        })) {
          if (child === item) break
          childrenToInsert.push(child)
          Transforms.removeNodes(editor, {
            at: childPath,
          })
        }

        if (childrenToInsert.length > 0) {
          // check for a list of same type at the end of our item
          const nestedListEntry = getListInItem(editor, itemPath)

          if (nestedListEntry) {
            // add stored siblings to the existing nested list
            const [nestedList, nestedListPath] = nestedListEntry
            Transforms.insertNodes(editor, childrenToInsert.reverse(), {
              at: nestedListPath.concat(nestedList.children.length),
            })
          } else {
            // add new nested list with stored siblings
            Transforms.insertNodes(
              editor,
              {
                type: list.type,
                children: childrenToInsert.reverse(),
              },
              {
                at: itemPath.concat(item.children.length),
              },
            )
          }
        }

        const itemPathRef = Editor.pathRef(editor, itemPath)
        Editor.withoutNormalizing(editor, () => {
          // lift our own item out of the list
          Transforms.liftNodes(editor, {
            at: itemPath,
          })
          Transforms.liftNodes(editor, {
            at: itemPathRef.current!,
          })
        })
      } else {
        // find the previous list item
        const previousItemEntry = Editor.previous(editor, {
          at: itemPath,
        })

        // don't do anything if this is the first item in the list or the previous item is not an element
        if (!previousItemEntry) return
        const [previousItem, previousItemPath] = previousItemEntry
        if (!Element.isElement(previousItem)) return

        // check if there's already a list at the end of the previous item
        const previousListEntry = getListInItem(editor, previousItemPath)

        // get a pathRef for the item because that's the only way to keep a valid path
        // while moving it around
        const itemPathRef = Editor.pathRef(editor, itemPath)

        Editor.withoutNormalizing(editor, () => {
          if (previousListEntry) {
            // move our item into the previous list
            const [previousList, previousListPath] = previousListEntry
            Transforms.moveNodes(editor, {
              at: itemPath,
              to: previousListPath.concat(previousList.children.length),
            })
          } else {
            const targetPath = previousItemPath.concat(
              previousItem.children.length,
            )
            Transforms.insertNodes(
              editor,
              { type: list.type, children: [] },
              {
                at: targetPath,
              },
            )
            Transforms.moveNodes(editor, {
              at: itemPath,
              to: targetPath.concat(0),
            })
          }

          // now unwrap nested lists from the current item if they are the same list type
          const nestedListEntry = getListInItem(editor, itemPathRef.current!)
          if (nestedListEntry && nestedListEntry[0].type === list.type) {
            const [nestedList, nestedListPath] = nestedListEntry
            Transforms.removeNodes(editor, { at: nestedListPath })
            Transforms.insertNodes(editor, nestedList.children, {
              at: Path.next(itemPathRef.current!),
            })
          }
        })
      }

      return
    }
  }

  // literally insert tab character
  Editor.insertText(editor, '\t')
}

function getListInItem(
  editor: Editor,
  itemPath: Path,
): ElementEntry | undefined {
  // get the last child of the target item
  const [childEntry] = Node.children(editor, itemPath, {
    reverse: true,
  })
  if (!childEntry) return
  const [child, childPath] = childEntry

  // if it's a list, return it
  if (Element.isElement(child) && LIST_TYPES.includes(child.type)) {
    return [child, childPath]
  }
}
